<template>
  <div :style="$root.isSupportMode ? { '--app-banner-h': '30px' } : null">
    <open-in-browser
      v-if="showOpenAppInBrowserScreen"
      :is-ios="isIos"
      @open="() => openInBrowser()"
    />
    <template v-else>
      <div
        v-if="$root.isSupportMode"
        class="fixed inset-x-0 top-0 z-50 flex h-[var(--app-banner-h)] items-center justify-center gap-1 bg-primary px-3 text-xs font-semibold tracking-wide text-primary-foreground shadow-lg"
      >
        <template v-if="!$root.loggedInUser">
          {{ tr('Support mode') }}
        </template>
        <template v-else>
          {{ tr('Company') }}:

          <template v-if="$root.currentCompany.name">
            {{ $root.currentCompany.name }}
          </template>
          <i v-else class="font-normal">{{ tr('unnamed company') }}</i>
        </template>
      </div>

      <ui-app-shell
        v-if="isSidebarVisible"
        :menu-label="tr('Open navigation menu')"
      >
        <template #sidebar="{ close }">
          <app-sidebar @navigate="() => close()" />
        </template>

        <!-- Page titles, actions and tabs are teleported here by PageHeader. -->
        <template #header-start>
          <div id="app-header-start" class="flex min-w-0 items-center gap-2" />
        </template>
        <template #header>
          <div id="app-header-title" class="flex min-w-0 items-center gap-2" />
        </template>
        <template #header-end>
          <div id="app-header-actions" class="flex items-center gap-2" />
        </template>
        <!-- Page tabs; hidden while no page puts anything here. -->
        <template #header-bottom>
          <div id="app-header-bottom" class="border-t border-border/60 p-1.5 [&:not(:has(*))]:hidden" />
        </template>

        <div class="mx-auto w-full max-w-[1440px] px-5 pb-5">
          <loader-circle-icon v-if="isLoading" class="fixed top-1/2 left-1/2 size-8 -translate-1/2 animate-spin text-primary" />
          <router-view v-else />
        </div>
      </ui-app-shell>

      <div v-else class="pt-[var(--app-banner-h,0px)]">
        <loader-circle-icon v-if="isLoading" class="fixed top-1/2 left-1/2 size-8 -translate-1/2 animate-spin text-primary" />
        <router-view v-else />
      </div>
    </template>

    <fullscreen-modal-host />
    <ui-toaster position="bottom-left" :duration="10000" close-button />
  </div>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import UserService from '@/services/User';
import AppSidebar from '@/ui/components/AppSidebar.vue';
import FullscreenModalHost from '@/ui/components/FullscreenModalHost.vue';
import OpenInBrowser from '@/ui/components/OpenInBrowser.vue';

export default {
  name: 'App',
  components: {
    AppSidebar,
    FullscreenModalHost,
    LoaderCircleIcon: LoaderCircle,
    OpenInBrowser,
  },
  // PageHeader moves page titles/actions into the shell header when it is shown.
  provide() {
    return {
      hasShellHeader: () => this.isSidebarVisible,
    };
  },
  computed: {
    userAgent() {
      return navigator.userAgent || navigator.vendor || window.opera;
    },
    isInAppBrowser() {
      return this.userAgent.indexOf('FBAN') != -1 || this.userAgent.indexOf('FBAV') !== -1;
    },
    isAndroid() {
      return this.userAgent.indexOf('Android') !== -1;
    },
    isIos() {
      return this.userAgent.indexOf('iPhone') !== -1 || this.userAgent.indexOf('iPad') !== -1;
    },
    showOpenAppInBrowserScreen() {
      return this.isInAppBrowser;
    },
    language() {
      return this.$store.getters['app/language'];
    },
    isLoading() {
      if (this.$root.isSupportMode && this.$store.getters['support/isFetching']) {
        return true;
      }

      return this.$store.getters['app/isLoading'] || this.$root.isUserFetching;
    },
    isSidebarVisible() {
      return this.$root.isLoggedIn
        && !this.$route.path.startsWith('/auth')
        && this.$route.path !== '/enable-2fa';
    },
  },
  watch: {
    '$root.loggedInUser'() {
      this.rebootInfiniteChat();
    },
    '$root.currentCompany.id': {
      immediate: true,
      handler(newId) {
        this.$store.dispatch('inAppNotifications/reset');

        if (this.$root.isLoggedIn && newId) {
          this.$store.dispatch('inAppNotifications/startPolling', newId);
        }
      },
    },
    language() {
      this.rebootInfiniteChat();
      if (this.$root.isLoggedIn) {
        UserService.changeLanguage(this.$root.loggedInUser.id, this.language);

        this.$store.dispatch('inAppNotifications/reset');
        this.$store.dispatch('inAppNotifications/startPolling', this.$root.currentCompany.id);
      }
    },
    '$route.path'() {
      this.$nextTick(() => {
        // Pageview.
        if (window.gtag) {
          window.gtag('event', 'conversion', { 'send_to': 'AW-11565729405/Yo9JCMuK6YwaEP2M_Ior' });
        }
      });
    },
  },
  created() {
    if (this.showOpenAppInBrowserScreen) {
      this.openInBrowser();
      return;
    }

    this.$store.dispatch('app/fetch');

    if (this.$root.isSupportMode && this.$store.getters['support/hasToken']) {
      this.$root.fetchSupportUserData();
    }

    this.rebootInfiniteChat();

    // eslint-disable-next-line no-console
    console.log('\n\n   88888888888888888888888888\n 888888888888888888888888888888\n88888888888888888888888888888888\n88888888888888888888888888888888\n8888888         8888888888888888\n888888    888    888888888888888\n888888     888           8888888\n88888888          8888    888888\n888888888888888     888   888888\n88888888888888888        8888888\n88888888888888888888888888888888\n 888888888888888888888888888888\n   88888888888888888888888888\n\n\n');
  },
  mounted() {
    this.$nextTick(() => {
      const isShoprenterAuthFlow = this.$app.isShoprenterApp && [ '/shoprenter/install', '/auth/shoprenter' ].includes(window.location.pathname);
      if (!isShoprenterAuthFlow && this.$store.getters['auth/hasToken']) {
        this.$root.fetchUserData().catch(err => this.error(err));
      }
      else {
        this.$store.commit('app/isLoading', false);
      }
    });
  },
  methods: {
    openInBrowser() {
      if (this.isAndroid) {
        window.location = 'intent:' + window.location + '#Intent;end';
      }
      else if (this.isIos) {
        const currentUrl = window.location.href;
        window.open(currentUrl, '_system');
      }
    },
    async rebootInfiniteChat() {
      if (!window.InfiniteChat || typeof window.InfiniteChat !== 'function') {
        await new Promise(resolve => setTimeout(resolve, 100));
        return this.rebootInfiniteChat();
      }

      // eslint-disable-next-line new-cap
      window.InfiniteChat('shutdown');

      let chatParams = {
        app_url: 'https://help.infinite.tools',
        language: this.language,
      };

      if (this.$root.loggedInUser) {
        chatParams = Object.assign(chatParams, {
          email: this.$root.loggedInUser.email,
          name: this.$root.loggedInUser.fullname,
          user_id: this.$root.loggedInUser.id,
          created_at: Math.floor((new Date(this.$root.loggedInUser.created_at)).getTime() / 1000),
          company: {
            id: this.$root.loggedInUser.company.id,
            name: this.$root.loggedInUser.company.name ?? this.$root.loggedInUser.company.billing_company,
            plan: this.$root.loggedInUser.company.current_subscription ? this.$root.loggedInUser.company.current_subscription.package?.name : '-',
            created_at: Math.floor((new Date(this.$root.loggedInUser.company.created_at)).getTime() / 1000),
          },
        });

        chatParams['Support Login'] = 'https://support.infinite.ad/?user_id=' + this.$root.loggedInUser.id;
      }

      // eslint-disable-next-line new-cap
      window.InfiniteChat('boot', chatParams);
      return undefined;
    },
  },
};
</script>
